import { useDataCatalog } from "@/composable/data-catalog";
import {
	VibrationAccelerationDataType,
	VibrationAxis,
	VibrationSubType,
	VibrationVelocityDataType,
} from "@soft-db/data-formatter";
import { FieldFactory } from "@soft-db/form";
import { Show } from "@soft-db/soft-utils";
import { SoftSelect } from "@soft-db/ui";
import { PropType, computed, defineComponent } from "vue";
import * as z from "zod";
import { wheneverOnce } from "../../composable/whenever-once";
import FrequencyInput from "./FrequencyInput";
import { QueryDataConfigForm } from "./QueryDataConfig";
import SpectrumSelection from "./SpectrumSelection";

export default defineComponent({
	name: "VibrationDataConfigSettings",
	components: { SpectrumSelection },
	props: {
		subType: {
			type: String as PropType<VibrationSubType>,
			required: true,
		},
		useField: {
			type: Function as PropType<FieldFactory<QueryDataConfigForm>>,
			required: true,
		},
		showSpectrum: Boolean,
		showFrequency: Boolean,
	},
	setup(props) {
		const { t } = useI18n();

		const data = props.useField("$dataConfig.data", {
			schema: computed(() =>
				props.subType === VibrationSubType.ACCELERATION
					? z.nativeEnum(VibrationAccelerationDataType)
					: z.nativeEnum(VibrationVelocityDataType)
			),
		});

		const isMaxHoldLegacy = ref(false);

		wheneverOnce(
			data.data,
			(data) => {
				if (data === "max_hold") {
					isMaxHoldLegacy.value = true;
				}
			},
			{ immediate: true }
		);

		const axis = props.useField("$dataConfig.axis", { schema: z.nativeEnum(VibrationAxis) });

		const spectrum = props.useField("$dataConfig.spectrum", {
			isRequired: computed(() => props.showSpectrum),
		});

		const {
			generateMenuEntries,
			VibrationAcceleration,
			VibrationVelocity,
			VibrationAxis: vibrationAxisMap,
		} = useDataCatalog();

		const dataTypes = computed(() => {
			const collection =
				props.subType === "acceleration" ? VibrationAcceleration : VibrationVelocity;

			if (!isMaxHoldLegacy.value) {
				// We need to typecast as any, since the max_hold is not optional in the type
				delete (collection as any).max_hold;
			}

			return generateMenuEntries(collection);
		});

		const vibAxis = generateMenuEntries(vibrationAxisMap);

		return () => (
			<>
				<SoftSelect
					label={t("common.dataType")}
					items={dataTypes.value}
					itemText="name"
					itemValue="value"
					required
					{...data.attrs}
				/>
				<SoftSelect
					label={t("dashboard.axis")}
					items={vibAxis}
					itemText="name"
					itemValue="value"
					required
					{...axis.attrs}
				/>
				<Show when={props.showSpectrum}>
					<SpectrumSelection v-model={spectrum.data.value} />
				</Show>

				<Show when={props.showFrequency}>
					<FrequencyInput useField={props.useField} />
				</Show>
			</>
		);
	},
});
